import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Lang } from '@crosscastr/shared';
import { api } from '../lib/api.js';
import { Icon } from '../components/Icon.js';
import { EmptyState } from '../components/EmptyState.js';
import { TableSkeleton } from '../components/Skeleton.js';

// "מקורות חדשות" — the newsroom autopilot (WOW wave #2). Manage RSS/news sources (feed, target
// groups, languages, auto-publish) and work the approval inbox of pending items. RTL + design tokens.

const EXTRA_LANGS: Lang[] = ['ar', 'ru'];
const LANG_LABEL: Record<Lang, string> = { he: 'עברית', ar: 'ערבית', ru: 'רוסית' };

const inputStyle = {
  fontSize: 13,
  padding: '8px 10px',
  borderRadius: 'var(--r-sm)',
  border: '1px solid var(--hair)',
  background: 'var(--surface-1)',
  color: 'var(--text)',
} as const;

export function NewsPage(): JSX.Element {
  const qc = useQueryClient();
  const { data: sources, isLoading } = useQuery({ queryKey: ['newsSources'], queryFn: () => api.newsSources() });
  const { data: items } = useQuery({ queryKey: ['newsItems'], queryFn: () => api.newsItems('pending') });
  const { data: targets } = useQuery({ queryKey: ['targets'], queryFn: () => api.targets() });
  const waGroups = (targets ?? []).filter((t) => t.surface === 'wa_group');

  const [feedUrl, setFeedUrl] = useState('');
  const [name, setName] = useState('');
  const [targetSel, setTargetSel] = useState<Set<string>>(new Set());
  const [langSel, setLangSel] = useState<Set<Lang>>(new Set());
  const [autoPublish, setAutoPublish] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['newsSources'] });
    void qc.invalidateQueries({ queryKey: ['newsItems'] });
  };

  const createMut = useMutation({
    mutationFn: () =>
      api.createNewsSource({
        feedUrl: feedUrl.trim(),
        name: name.trim(),
        targetIds: [...targetSel],
        languages: [...langSel],
        autoPublish,
      }),
    onSuccess: () => {
      setFeedUrl(''); setName(''); setTargetSel(new Set()); setLangSel(new Set()); setAutoPublish(false);
      setToast('המקור נוסף ✓');
      invalidate();
    },
    onError: () => setToast('הוספת המקור נכשלה — בדקו את הכתובת ואת הקבוצות'),
  });

  const toggleActiveMut = useMutation({
    mutationFn: (v: { id: string; active: boolean }) => api.updateNewsSource(v.id, { active: v.active }),
    onSuccess: invalidate,
  });
  const toggleAutoMut = useMutation({
    mutationFn: (v: { id: string; autoPublish: boolean }) => api.updateNewsSource(v.id, { autoPublish: v.autoPublish }),
    onSuccess: () => { setToast('עודכן מצב פרסום אוטומטי'); invalidate(); },
  });
  const deleteMut = useMutation({ mutationFn: (id: string) => api.deleteNewsSource(id), onSuccess: () => { setToast('המקור הוסר'); invalidate(); } });
  const approveMut = useMutation({ mutationFn: (id: string) => api.approveNewsItem(id), onSuccess: () => { setToast('ההודעה אושרה ונשלחה ✓'); invalidate(); } });
  const skipMut = useMutation({ mutationFn: (id: string) => api.skipNewsItem(id), onSuccess: invalidate });

  function toggleTarget(id: string): void {
    setTargetSel((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }
  function toggleLang(l: Lang): void {
    setLangSel((prev) => { const n = new Set(prev); if (n.has(l)) n.delete(l); else n.add(l); return n; });
  }
  const canCreate = feedUrl.trim() && name.trim() && targetSel.size > 0 && !createMut.isPending;

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">פרסום אוטומטי</div>
          <h1>פרסום אוטומטי</h1>
          <div className="sub">חברו כתובת של אתר חדשות → כל כתבה חדשה הופכת אוטומטית להודעה מוכנה (מסוכמת, מתורגמת, עם קישור) — לשליחה מיד או לאישור שלכם.</div>
        </div>
      </div>

      {/* Add source */}
      <div className="card" style={{ padding: 22, marginBottom: 20 }}>
        <div className="card-h" style={{ padding: 0, marginBottom: 14 }}><h3>הוספת מקור</h3></div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <input dir="ltr" value={feedUrl} onChange={(e) => setFeedUrl(e.target.value)} placeholder="https://example.com/feed" style={{ ...inputStyle, flex: 2, minWidth: 220 }} />
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="שם המקור (למשל: Ynet מבזקים)" style={{ ...inputStyle, flex: 1, minWidth: 160 }} />
          </div>

          <div>
            <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 6 }}>לאילו קבוצות לשלוח</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {waGroups.length === 0 && <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>עדיין לא הוגדרו קבוצות WhatsApp</span>}
              {waGroups.map((g) => (
                <button key={g.id} type="button" onClick={() => toggleTarget(g.id)} className={`tool ${targetSel.has(g.id) ? 'var' : ''}`} style={{ fontSize: 12 }}>
                  {targetSel.has(g.id) ? '✓ ' : ''}{g.name}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>תרגום ל-</span>
              {EXTRA_LANGS.map((l) => (
                <button key={l} type="button" onClick={() => toggleLang(l)} className={`tool ${langSel.has(l) ? 'var' : ''}`} style={{ fontSize: 12 }}>
                  {langSel.has(l) ? '✓ ' : ''}{LANG_LABEL[l]}
                </button>
              ))}
            </div>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={autoPublish} onChange={(e) => setAutoPublish(e.target.checked)} /> פרסום אוטומטי (ללא אישור)
            </label>
            <button className="btn sheen-host sheen-hover" style={{ marginInlineStart: 'auto' }} onClick={() => createMut.mutate()} disabled={!canCreate}>
              <Icon name="plus" /> {createMut.isPending ? 'מוסיף…' : 'הוסף מקור'}
            </button>
          </div>
        </div>
      </div>

      {/* Sources list */}
      {isLoading || !sources ? (
        <div className="card"><TableSkeleton rows={3} /></div>
      ) : sources.length === 0 ? (
        <div className="card">
          <EmptyState icon="channel" title="עדיין אין מקורות" sub="הוסיפו כתובת של אתר חדשות למעלה — כל כתבה חדשה תזוהה אוטומטית ותחכה כאן לאישור, או תישלח לבד." />
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden', marginBottom: 24 }}>
          <table className="dtable">
            <thead><tr><th>מקור</th><th>כתובת</th><th>קבוצות</th><th>שפות</th><th>אוטומטי</th><th>ממתינים</th><th>פעולות</th></tr></thead>
            <tbody>
              {sources.map((s) => (
                <tr key={s.id} style={{ opacity: s.active ? 1 : 0.5 }}>
                  <td>{s.name}</td>
                  <td className="ltr" style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-dim)', fontSize: 12 }}>{s.feedUrl}</td>
                  <td className="mono">{s.targetIds.length}</td>
                  <td style={{ fontSize: 12 }}>{['he', ...s.languages].map((l) => LANG_LABEL[l as Lang]).join(', ')}</td>
                  <td>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 12 }}>
                      <input type="checkbox" checked={s.autoPublish} onChange={(e) => toggleAutoMut.mutate({ id: s.id, autoPublish: e.target.checked })} />
                    </label>
                  </td>
                  <td>{s.pendingCount > 0 ? <span className="mono" style={{ color: 'var(--accent-teal)', fontWeight: 600 }}>{s.pendingCount}</span> : '—'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn ghost" style={{ padding: '4px 8px', fontSize: 12 }} onClick={() => toggleActiveMut.mutate({ id: s.id, active: !s.active })}>
                        {s.active ? 'השהה' : 'הפעל'}
                      </button>
                      <button className="btn ghost" style={{ padding: '4px 8px' }} title="הסר" onClick={() => deleteMut.mutate(s.id)}><Icon name="close" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Approval inbox */}
      <div className="section-title"><h2 style={{ fontSize: 20 }}>ממתין לאישור</h2><span className="rule" /></div>
      {!items || items.length === 0 ? (
        <div className="card">
          <EmptyState icon="check" title="אין הודעות לאישור" sub="כשמקור לא-אוטומטי ימצא כתבה חדשה — היא תופיע כאן, ותוכלו לאשר או לדחות." />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {items.map((it) => (
            <div key={it.id} className="card" style={{ padding: 16, display: 'flex', gap: 14, alignItems: 'center' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{it.title || '(ללא כותרת)'}</div>
                <div style={{ display: 'flex', gap: 10, fontSize: 12, color: 'var(--text-dim)' }}>
                  <span>{it.sourceName}</span>
                  <a className="ltr" href={it.link} target="_blank" rel="noreferrer" style={{ color: 'var(--accent-teal)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 360 }}>{it.link}</a>
                </div>
              </div>
              <button className="btn ghost" onClick={() => skipMut.mutate(it.id)} disabled={skipMut.isPending}>דחה</button>
              <button className="btn sheen-host sheen-hover" onClick={() => approveMut.mutate(it.id)} disabled={approveMut.isPending}>
                <Icon name="send" /> אשר ושלח
              </button>
            </div>
          ))}
        </div>
      )}

      {toast && <Toast text={toast} onDone={() => setToast(null)} />}
    </>
  );
}

function Toast({ text, onDone }: { text: string; onDone: () => void }): JSX.Element {
  setTimeout(onDone, 2600);
  return <div className="toast"><span className="d" /> {text}</div>;
}
